import React from "react";
import SkeletonLoader from "components/ui/SkeletonLoader";
import './CandidateDetailPageSkeleton.css';

const CandidateDetailPageSkeleton: React.FC = () => (
  <div className="candidate-detail-page">
    <section className="candidate-detail-hero">
      {/* Hero Header Media */}
      <div className="candidate-detail-hero__media" style={{ backgroundColor: "#1a3c6e", position: "relative" }}>
        {/* Back Button Skeleton */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 14px", background: "#FFFFFF", borderRadius: 8, border: "1px solid #E2E8F0" }}>
          <SkeletonLoader width={14} height={14} circle />
          <SkeletonLoader width={42} height={14} />
        </div>

        {/* Media Tools (Score Card Overlay & Watch Pitch) */}
        <div className="candidate-detail-hero__media-tools">
          {/* Header Score Overlay Skeleton */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 14px",
              background: "rgba(255, 255, 255, 0.95)",
              borderRadius: 8,
              border: "1.5px solid rgba(255, 255, 255, 0.8)",
              minWidth: 200,
            }}
          >
            <SkeletonLoader width={38} height={38} circle />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <SkeletonLoader width={70} height={14} />
              <SkeletonLoader width={100} height={10} />
            </div>
          </div>

          {/* Watch Pitch Button Skeleton */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 14px",
              background: "rgba(255, 255, 255, 0.15)",
              border: "1.5px solid rgba(255, 255, 255, 0.3)",
              borderRadius: 8,
              backdropFilter: "blur(8px)",
            }}
          >
            <SkeletonLoader width={28} height={28} circle />
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <SkeletonLoader width={65} height={12} />
              <SkeletonLoader width={80} height={10} />
            </div>
          </div>
        </div>
      </div>

      {/* Hero Content Section */}
      <div className="candidate-detail-hero__content">
        <div className="candidate-detail-hero__identity">
          <div className="candidate-detail-hero__avatar-wrap">
            <SkeletonLoader width={108} height={108} circle />
          </div>

          <div className="candidate-detail-hero__copy" style={{ paddingTop: 31, flex: 1 }}>
            {/* Name + Status Pill + Contact Chips */}
            <div className="candidate-detail-hero__name-row" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <SkeletonLoader width={160} height={24} />
              <SkeletonLoader width={90} height={22} style={{ borderRadius: 99 }} />
              <SkeletonLoader width={150} height={26} style={{ borderRadius: 8 }} />
              <SkeletonLoader width={130} height={26} style={{ borderRadius: 8 }} />
            </div>

            {/* Meta subtitle */}
            <div className="candidate-detail-hero__meta" style={{ marginTop: 8, display: "flex", gap: 10 }}>
              <SkeletonLoader width={120} height={14} />
              <SkeletonLoader width={80} height={14} />
              <SkeletonLoader width={110} height={14} />
            </div>

            {/* Key Metrics Bar */}
            <div className="candidate-detail-metrics-bar" style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <SkeletonLoader width={100} height={24} style={{ borderRadius: 6 }} />
              <SkeletonLoader width={65} height={24} style={{ borderRadius: 6 }} />
              <SkeletonLoader width={80} height={24} style={{ borderRadius: 6 }} />
              <SkeletonLoader width={110} height={24} style={{ borderRadius: 6 }} />
            </div>
          </div>
        </div>

        {/* Hero Action Buttons */}
        <div className="candidate-detail-hero__actions" style={{ paddingTop: 43, display: "flex", gap: 10 }}>
          <SkeletonLoader width={138} height={44} style={{ borderRadius: 8 }} />
          <SkeletonLoader width={110} height={44} style={{ borderRadius: 8 }} />
          <SkeletonLoader width={44} height={44} style={{ borderRadius: 8 }} />
        </div>
      </div>
    </section>

    {/* Main Page Layout */}
    <div className="candidate-detail-layout" style={{ marginTop: 16 }}>
      <div className="candidate-detail-main">
        {/* Navigation Tabs (Overview, Experience, Skills, Job Match, Applications) */}
        <div className="candidate-detail-tabs" style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {["Overview", "Experience & Education", "Skills & More", "Job Match", "Applications"].map((tabName, index) => (
            <div
              key={`tab-skeleton-${index}`}
              style={{
                padding: "8px 16px",
                background: index === 0 ? "#FFFFFF" : "#F8FAFC",
                border: `1px solid ${index === 0 ? "#2563EB" : "#E2E8F0"}`,
                borderRadius: 8,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SkeletonLoader width={70 + index * 10} height={14} />
            </div>
          ))}
        </div>

        {/* Overview Stack */}
        <div className="candidate-detail-section-stack">
          {/* About Panel */}
          <section className="candidate-detail-panel">
            <div className="candidate-detail-panel__title" style={{ marginBottom: 12 }}>
              <SkeletonLoader width={80} height={18} />
            </div>
            <SkeletonLoader count={2} height={14} style={{ marginBottom: 8 }} />
          </section>

          {/* Current Role & Education Cards */}
          <div className="candidate-detail-overview-grid">
            <section className="candidate-detail-mini-card">
              <div className="candidate-detail-panel__title" style={{ marginBottom: 10 }}>
                <SkeletonLoader width={100} height={16} />
              </div>
              <SkeletonLoader width="70%" height={18} style={{ marginBottom: 8 }} />
              <SkeletonLoader width="50%" height={14} />
            </section>
            <section className="candidate-detail-mini-card">
              <div className="candidate-detail-panel__title" style={{ marginBottom: 10 }}>
                <SkeletonLoader width={120} height={16} />
              </div>
              <SkeletonLoader width="65%" height={18} style={{ marginBottom: 8 }} />
              <SkeletonLoader width="45%" height={14} />
            </section>
          </div>

          {/* Job Match Breakdown Card Skeleton */}
          <section className="cdp-job-matches" style={{ padding: "16px 18px", background: "#FFFFFF", borderRadius: 16, border: "1px solid #E2E8F0" }}>
            {/* Breakdown Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <SkeletonLoader width={120} height={32} style={{ borderRadius: 8 }} />
                <SkeletonLoader width={150} height={18} />
              </div>
              <SkeletonLoader width={110} height={32} style={{ borderRadius: 8 }} />
            </div>

            {/* Active Role Banner Skeleton */}
            <div
              style={{
                background: "linear-gradient(270deg, #437CFC 0%, #012883 100%)",
                borderRadius: 12,
                padding: "12px 16px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <div>
                <SkeletonLoader width={65} height={10} style={{ marginBottom: 4 }} />
                <SkeletonLoader width={180} height={18} />
              </div>
              {/* White Score Ring Badge Container */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "#FFFFFF",
                  padding: "5px 12px 5px 8px",
                  borderRadius: 8,
                }}
              >
                <SkeletonLoader width={36} height={36} circle />
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <SkeletonLoader width={55} height={12} />
                  <SkeletonLoader width={70} height={10} />
                </div>
              </div>
            </div>

            {/* 6-Factor Criteria Grid (3 per row) Skeleton */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={`criteria-skeleton-${index}`} style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <SkeletonLoader width={70} height={12} />
                    <SkeletonLoader width={40} height={12} />
                  </div>
                  <SkeletonLoader width="100%" height={4} style={{ borderRadius: 99 }} />
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Sidebar Section Skeleton */}
      <aside className="candidate-detail-sidebar">
        <section className="candidate-detail-match-card" style={{ borderRadius: 24, overflow: "hidden" }}>
          {/* Sidebar Match Tabs */}
          <div className="candidate-detail-match-tabs" style={{ padding: "8px 10px", background: "#FFFFFF" }}>
            <div style={{ background: "#F1F5F9", borderRadius: 6, padding: "8px", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <SkeletonLoader width={80} height={14} />
            </div>
            <div style={{ background: "#F1F5F9", borderRadius: 6, padding: "8px", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <SkeletonLoader width={95} height={14} />
            </div>
          </div>

          {/* Sidebar Company Match Header & Hero */}
          <div className="candidate-detail-match-card__header" style={{ padding: "14px 18px" }}>
            <div>
              <SkeletonLoader width={120} height={16} />
              <SkeletonLoader width={140} height={12} style={{ marginTop: 4 }} />
            </div>
          </div>
          <div className="candidate-detail-match-card__hero" style={{ background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)", padding: "18px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around" }}>
              <SkeletonLoader width={46} height={46} circle />
              <SkeletonLoader width={64} height={64} circle />
              <SkeletonLoader width={46} height={46} circle />
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
);

export default CandidateDetailPageSkeleton;
